export interface MetricContext {
  label: string;
  description: string;
  healthyBaseline: string;
  warning: string;
}

export const metricContextMap: Record<string, MetricContext> = {
  'cost.monthly_total': {
    label: 'Monthly Total',
    description: 'Total AWS spend for the current calendar month across all services.',
    healthyBaseline: "For Overhead's scale (~1 EC2, 1 RDS, S3 storage), expect $80–$150/month.",
    warning:
      'A sudden jump (>30% above last month) usually means a forgotten devbox or unexpected data transfer.',
  },
  'cost.daily_average': {
    label: 'Daily Average',
    description:
      'Average cost per day this month so far. Helpful for projecting the monthly total.',
    healthyBaseline: 'Should be roughly $3–$5/day.',
    warning: 'If the daily average suddenly doubles, check for orphaned EC2 instances.',
  },
  'cost.month_over_month': {
    label: 'Month-over-Month Change',
    description: 'Percentage change compared to the same point last month.',
    healthyBaseline: 'Should stay within +/- 15% unless you intentionally changed infrastructure.',
    warning: "Increases over 30% that you didn't plan for need immediate attention.",
  },
  'ec2.cpu_utilization': {
    label: 'CPU Utilization',
    description: "How much of the server's processing power is being used.",
    healthyBaseline: 'Normal is 5–20% during off-hours, 20–50% during peak.',
    warning: 'Sustained above 80% for >10 minutes means the server is struggling.',
  },
  'ec2.instance_count': {
    label: 'Running Instance Count',
    description: 'Number of EC2 instances tagged as production. Should be exactly one.',
    healthyBaseline: 'Exactly 1 running production instance.',
    warning: '0 = backend is down. 2+ = orphan from a failed deploy.',
  },
  'rds.cpu_utilization': {
    label: 'Database CPU',
    description: 'CPU usage of the PostgreSQL database server.',
    healthyBaseline: 'Usually 5–15%. Spikes during report generation are normal.',
    warning: 'Sustained above 70% means expensive queries. Check for missing indexes.',
  },
  'rds.connection_count': {
    label: 'Active Connections',
    description: 'Number of open database connections.',
    healthyBaseline: 'Backend pool max is 20. Normal: 2–10.',
    warning: 'Approaching 20 means the pool is saturated.',
  },
  'rds.freeable_memory': {
    label: 'Freeable Memory',
    description: 'RAM available for the database.',
    healthyBaseline: 'At least 30% should be free.',
    warning: 'Below 20% free means the database may start swapping to disk.',
  },
  'rds.storage_used': {
    label: 'Free Storage',
    description: 'Remaining disk space on the database.',
    healthyBaseline: 'With 26-month partition retention, expect steady state.',
    warning: 'Rapid growth (>10%/week) needs investigation. Running out crashes the database.',
  },
  's3.overview': {
    label: 'S3 Storage',
    description:
      'Amazon S3 stores files in "buckets." Overhead uses two: one for the customer-facing frontend files (JS, CSS, HTML) and one for raw POS data received from restaurant integrations.',
    healthyBaseline:
      'Frontend bucket stays small (< 100 MB). Raw data bucket grows steadily with POS ingestion — this is expected.',
    warning:
      'If the raw data bucket grows unusually fast, check for duplicate POS sync runs. If the frontend bucket is empty, a deploy may have failed.',
  },
  'cloudfront.overview': {
    label: 'CloudFront CDN',
    description:
      'CloudFront is a content delivery network that serves the frontend to users. It caches files at edge locations worldwide so pages load faster.',
    healthyBaseline:
      'Should show "Deployed" status. Error rates near 0%. Request counts depend on active users.',
    warning:
      'Error rates above 5% may mean a bad frontend deploy (missing files in S3). A huge request spike could indicate bot traffic.',
  },
  'efs.overview': {
    label: 'Elastic File System',
    description:
      'EFS is shared network storage mounted on the EC2 server. Overhead uses it to store TLS certificates (so they survive instance replacements during deploys) and temporarily stage POS data before uploading to S3.',
    healthyBaseline:
      'Should be very small (under 1 GB). TLS certs are tiny and shard data is temporary.',
    warning:
      'If EFS grows beyond 5 GB, shard finalization may be stuck — data is accumulating locally instead of moving to S3.',
  },
  'route53.overview': {
    label: 'Route 53 DNS',
    description:
      'Route 53 manages DNS records — the mapping from domain names (like api.getoverhead.com) to server IP addresses. This table shows all DNS records in the Overhead hosted zone.',
    healthyBaseline:
      'Records should match known infrastructure. api.getoverhead.com should point to the production Elastic IP. Devbox records are created/deleted automatically.',
    warning:
      'Unknown or stale records (especially A records pointing to terminated instances) should be cleaned up.',
  },
  'devbox.count': {
    label: 'Active Devboxes',
    description: 'Running devbox EC2 instances. Each costs ~$0.02/hr.',
    healthyBaseline: 'Usually 0–2. Auto-shutdown after 12 hours.',
    warning: 'More than 2 running with nobody using them should be torn down.',
  },
  'devbox.uptime': {
    label: 'Devbox Uptime',
    description: 'How long a devbox has been running since launch.',
    healthyBaseline: 'Active dev sessions last 2–8 hours.',
    warning: 'Running longer than 12 hours may have failed to auto-terminate.',
  },
};
